import React, { useState, useEffect, Fragment } from "react";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Label } from "@progress/kendo-react-labels";
import { RadioGroup } from "@progress/kendo-react-inputs";
import { Editor, EditorTools } from "@progress/kendo-react-editor";
import { shareDocument, getSuggestedEmails } from "api";
import { fileIcon } from "components/common/fileIcon";
import styles from "../CaseFile/CaseWorkFlow/CaseWorkflow.module.scss";
import { handleApiError } from "Helper";
import EmailListRenderer from "components/common/EmailComposition/EmailListRenderer";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { toast } from "react-toastify";
import SubmitButton from "components/common/SubmitButton";
import { useApplicationSettings } from "services/applicationSettingsService";
import EmailDialog from "components/common/EmailComposition/EmailDialog";
const {
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignRight,
  AlignCenter,
  Indent,
  Outdent,
} = EditorTools;

const DocumentItem = ({ doc }) => {
  const getDisplayName = (name) => {
    if (!name) {
      return;
    }
    if (name.length <= 20) {
      return name;
    }
    const extension = name.includes(".") ? "." + name.split(".").pop() : "";
    return name?.substring(0, 20 - extension.length) + extension;
  };

  return (
    <div className={styles["document-item"]}>
      {fileIcon(doc.name)}
      <div>
        <span className="document-name-container ps-2 fs-sm ">
          {getDisplayName(doc?.name)}
        </span>
      </div>
    </div>
  );
};

const ShareDocumentModal = (props) => {
  const { data: applicationSettings } = useApplicationSettings();
  const [email, setEmail] = useState("");
  const [shareType, setShareType] = useState(1); 
  const selectedDocuments = props?.selectedDocuments;
  const currentDoc = props?.currentDoc;
  const [editorValue, setEditorValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [Error, setError] = useState(false);
  const [suggestedEmails, setSuggestedEmails] = useState([]);

  const defaultEmailClient = applicationSettings?.find(x => x.key === "DefaultEmailClient")?.value;
  const isOfficeWebMail = defaultEmailClient === "OfficeWebMail";

  const allFilesArePDFs =
    selectedDocuments?.length >= 1
      ? selectedDocuments.every((doc) => doc?.name?.endsWith(".pdf"))
      : currentDoc?.name?.endsWith(".pdf");

  const ShareType = [
    {
      label: "As Attachment",
      value: 1,
    },
    ...(!allFilesArePDFs
      ? [
        {
          label: "As PDF Attachment",
          value: 2,
        },
      ]
      : []),
    {
      label: "As File Link",
      value: 3,
    },
  ];

  useEffect(() => {
    const fetchSuggestedEmails = async () => {
      try {
        const emails = await getSuggestedEmails(props.matterId);
        setSuggestedEmails(emails?.data?.data);
      } catch (error) {
        console.error("Failed to fetch suggested emails", error);
      } finally {
      }
    };

    fetchSuggestedEmails();
  }, []);

  const handleShareDocument = async () => {
    if (!email || !editorValue || editorValue==="<p></p>") {
      setError(true);
      return;
    }
    
    setIsLoading(true);

    const data = {
      toRecipients: [...email?.email?.split(",")],
      htmlBody: editorValue,
      isHtmlBody: true,
      linkExpiryDays: 0,
      attachmentDocumentIDs: [
        ...(selectedDocuments?.length > 0
          ? selectedDocuments.map((doc) => doc.id)
          : [currentDoc?.id]),
      ],
      shareType: shareType,
    };

    try {
      await shareDocument(props.matterId, data);
      toast.success("Document shared successfully!");
      props.onClose();
    } catch (error) {
      handleApiError(error);
      toast.error("An error occurred while sharing the document.");
    }
    setIsLoading(false);
  };
  const handleEmailChange = (event) => {
    setEmail(event.value);
  };

  const itemRender = (li, itemProps) => (
    <EmailListRenderer li={li} itemProps={itemProps} />
  );

  return (
    <Fragment>
      {isOfficeWebMail ? (
        <EmailDialog
          title={"Share Document"}
          matterId={props.matterId}
          matter={props.matter}
          onClose={() => props.onClose()}
          openExistingEmailDialog={(e) => {
            props.openExistingEmailDialog(e);
          }}
          isShareDocumentMode={true}
          documentIds={
            selectedDocuments?.length > 0
              ? selectedDocuments.map((doc) => doc.id)
              : [currentDoc?.id]
          }
          shareType={shareType}
        />
      ) : (
      <Dialog title={"Share Document"} onClose={props.onClose} className="dialog-xs">
      <div className={`px-5 py-4`}>
        <div className="d-flex justify-content-between flex-wrap mb-2">

          <div className="d-flex flex-column mb-2">
            <Label className="fs-sm mb-1">Email</Label>
            <ComboBox
              placeholder="Enter Email"
              style={{
                width: "300px",
                border: (Error && email === "") ? "1px solid #dc3545" : undefined,
              }}
              data={suggestedEmails}
              textField="email"
              value={email}
              onChange={handleEmailChange}
              itemRender={itemRender}
              allowCustom
            />
          </div>

          <div className="d-flex align-items-start mt-3 ">
            <RadioGroup
              data={ShareType}
              value={shareType}
              className="radio"
              required
              onChange={(e) => setShareType(e.value)}
            />
          </div>
        </div>
        <div className="d-flex flex-wrap mt-2 mb-3">
          {selectedDocuments?.length > 0 ? (
            selectedDocuments?.map((doc, index) => (
              <DocumentItem key={doc.id} doc={doc} />
            ))
          ) : currentDoc ? (
            <DocumentItem key={currentDoc.documentId} doc={currentDoc} />
          ) : null}
        </div>

        <Editor
          tools={[
            [Bold, Italic, Underline],
            [AlignLeft, AlignCenter, AlignRight],
            [Indent, Outdent],
          ]}
        style={
          (Error && (editorValue === "" || editorValue==="<p></p>"))
            ? { border: "1px solid #dc3545", borderRadius: "4px" }
            : undefined
        }
          contentStyle={{
            height: 260,
          }}
          defaultContent={""}
          value={editorValue}
          onChange={(event) => {
            setEditorValue(event.html);
          }}
        />
      </div>

      <div className="dialog-footer">
        <SubmitButton
          className="common-btn me-4"
          type="submit"
          isLoading={isLoading}
          disabled={isLoading}
          onClick={handleShareDocument}
        >
          Share
        </SubmitButton>
      </div>
       </Dialog>
    )}
    </Fragment>
   
  );
};

export default ShareDocumentModal;
